<script setup lang="ts">
import { computed, getCurrentInstance } from "vue";

import { useSignedUrl } from "@/composables/useSignedUrl";
import { formatFullName } from "@/helpers/name";
import { removeAccents } from "@/helpers/string";

import type { BottomAvatarStatus } from "./types";

import Profil from "./Profil.vue";
import { AvatarColor, AvatarSize } from "./types";

export interface AvatarProps {
    initials?: string;
    firstname?: string;
    lastname?: string;
    color?: keyof typeof AvatarColor;
    size?: AvatarSize | keyof typeof AvatarSize;
    imageUrl?: string | File | null;
    displayName?: boolean;
    fontWeight?: string | number;
    fontSize?: string;
    bottomStatus?: BottomAvatarStatus;
    gap?: string;
    isSuggested?: boolean;
}

const props = withDefaults(defineProps<AvatarProps>(), {
    size: AvatarSize.size16,
    fontWeight: 500,
    fontSize: "inherit",
    gap: "12px",
});

const formattedUrl = useSignedUrl(() => props.imageUrl);

const alphabet = "abcdefghijklmnopqrstuvwxyz";
const colors = [AvatarColor.blue, AvatarColor.purple, AvatarColor.red, AvatarColor.orange, AvatarColor.yellow, AvatarColor.green];

const getInitials = (considerSize = true) => {
    const fromFirstLastName = [props.firstname, props.lastname]
        .filter(Boolean)
        .map((name) => {
            if (name) {
                for (let index = 0; index < name.length; index++) {
                    if (alphabet.includes(name.charAt(index).toLowerCase())) {
                        return name.charAt(index);
                    }
                }
            }
            return "";
        })
        .join("")
        .toUpperCase();
    if (!props.initials && !fromFirstLastName) return null;
    const finalInitials = props.initials ?? fromFirstLastName;
    const oneLetterSize = [AvatarSize.size24, AvatarSize.size20, AvatarSize.size16];
    return oneLetterSize.includes(props.size as AvatarSize) && considerSize ? finalInitials.charAt(0) : finalInitials;
};

const displayInitials = computed<string | null>(() => getInitials());
const fullName = computed(() => formatFullName(props));

const getAvatarColorFromName = (fullName: string) => {
    const normalizedName = removeAccents(fullName.toLowerCase());
    if (!normalizedName) {
        const randomIndex = Math.floor(Math.random() * colors.length);
        return colors[randomIndex];
    }

    let hash = 0;
    for (let index = 0; index < normalizedName.length; index++) {
        hash = normalizedName.charCodeAt(index) + ((hash << 5) - hash);
    }

    const colorIndex = Math.abs(hash) % colors.length;
    return colors[colorIndex];
};

const computedColor = computed(() => {
    if (props.color) {
        return AvatarColor[props.color];
    }

    if (fullName.value) {
        return getAvatarColorFromName(fullName.value);
    }

    const initials = getInitials(false);
    if (initials) {
        return getAvatarColorFromName(initials);
    }

    const randomIndex = Math.floor(Math.random() * colors.length);
    return colors[randomIndex];
});

const avatarComponentClasses = computed(() => {
    return ["ds-avatar-component", props.size, { clickable: !!getCurrentInstance()?.vnode.props?.onClick }];
});

const emit = defineEmits<(e: "click", event: Event) => void>();

const handleClick = (e: Event) => {
    emit("click", e);
};
</script>
<template>
    <div :class="avatarComponentClasses" :style="{ gap }" @click="handleClick">
        <div class="avatar-bottom-status-container">
            <div :class="['avatar', size, computedColor, { suggested: isSuggested }]">
                <img v-if="formattedUrl" :src="formattedUrl" alt="Avatar image" />
                <span v-else-if="displayInitials">{{ displayInitials }}</span>
                <Profil v-else />
            </div>
            <div v-if="bottomStatus || isSuggested" :class="['bottom-status', isSuggested ? 'suggested' : bottomStatus?.color]">
                <i :class="`ti ti-${isSuggested ? 'sparkles-fill' : bottomStatus?.icon}`" />
            </div>
        </div>
        <span class="name" v-if="displayName" :style="{ fontWeight, fontSize }">{{ fullName }}</span>
    </div>
</template>
<style scoped lang="scss">
@mixin bg-ang-color($bg, $color, $profil-color) {
    background-color: $bg;
    color: $color;

    .Profil {
        color: $profil-color;
    }
}

.ds-avatar-component {
    display: flex;
    align-items: center;

    &.clickable {
        cursor: pointer;
    }

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
    }

    .avatar {
        display: flex;
        border-radius: 50px;
        overflow: hidden;

        &.suggested {
            border: 2px solid var(--blue-base, #2051f8);
        }
    }

    .avatar-bottom-status-container {
        position: relative;
    }

    .bottom-status {
        right: -6px;
        bottom: -6px;
        display: flex;
        position: absolute;
        border-radius: 50px;
        align-items: center;
        justify-content: center;

        &.suggested {
            color: var(--blue-base, #2051f8);
            background-color: var(--blue-lighter, #ebefffff);
        }
    }

    .avatar > * {
        margin: auto;
    }

    .Profil {
        margin-top: auto;
        margin-bottom: 0;
    }

    .name {
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .blue {
        @include bg-ang-color($blue-lighter, $blue-dark, $blue-400);
    }

    .purple {
        @include bg-ang-color($purple-lighter, $purple-dark, $purple-400);
    }

    .red {
        @include bg-ang-color($red-lighter, $red-dark, $red-400);
    }

    .orange {
        @include bg-ang-color($orange-lighter, $orange-dark, $orange-400);
    }

    .yellow {
        @include bg-ang-color($yellow-lighter, $yellow-dark, $yellow-400);
    }

    .green {
        @include bg-ang-color($green-lighter, $green-dark, $green-400);
    }

    // New size
    &.size-80 {
        .avatar {
            @include font-size-32;
            @include square(80px);
        }

        .bottom-status {
            @include square(36px);
            @include font-size-18;
        }
    }

    &.size-72 {
        .avatar {
            @include square(72px);
            @include font-size-24;
        }

        .bottom-status {
            @include square(36px);
            @include font-size-18;
        }
    }

    &.size-64 {
        .avatar {
            @include square(64px);
            @include font-size-24;
        }

        .bottom-status {
            @include square(32px);
            @include font-size-16;
        }
    }

    &.size-56 {
        .avatar {
            @include square(56px);
            @include font-size-20;
        }

        .bottom-status {
            @include square(28px);
            @include font-size-14;
        }
    }

    &.size-48 {
        .avatar {
            @include square(48px);
            @include font-size-18;
        }

        .bottom-status {
            @include square(24px);
            @include font-size-12;
        }
    }

    &.size-40 {
        .avatar {
            @include square(40px);
            @include font-size-16;
            .Profil {
                height: 80%;
            }
        }

        .bottom-status {
            @include square(24px);
            @include font-size-12;
        }
    }

    &.size-32 {
        .avatar {
            @include square(32px);
            @include font-size-13;
            .Profil {
                height: 80%;
            }
        }

        .bottom-status {
            @include square(20px);
            @include font-size-10;
        }
    }

    &.size-24 {
        padding: 5px 0;

        .avatar {
            @include square(24px);
            @include font-size-12;
            .Profil {
                height: 80%;
            }
        }

        .bottom-status {
            @include square(16px);
            font-size: 8px;
        }
    }

    &.size-20 {
        .avatar {
            @include square(20px);
            @include font-size-11;
            .Profil {
                height: 80%;
            }
        }

        .bottom-status {
            @include square(14px);
            font-size: 7px;
        }
    }

    &.size-16 {
        .avatar {
            @include square(16px);
            @include font-size-10;
            .Profil {
                height: 80%;
            }
        }

        .bottom-status {
            @include square(11.2px);
            font-size: 5.6px;
        }
    }

    .size-14 {
        font-size: 10px;
        line-height: 14px;
        @include square(14px);
        .Profil {
            height: 80%;
        }
    }
}
</style>
